/**
 * Coach Engine — Session Scope
 *
 * Orchestrates session-scope analyzers. Takes a session object and the
 * user's full trading context, builds an analyzer context, runs every
 * analyzer, and returns ranked observations.
 *
 * Adding a new session-scope analyzer:
 *   1. Create the module under ../analyzers/
 *   2. Import + push into ANALYZERS below
 *   3. Done — ranking, dedup, narrator all wire up automatically.
 */

import { withNetPnl, summarizePnl } from '../utils';
import { topObservations } from '../priorities';
import { SEVERITY, CATEGORY, makeObservation } from '../types';
import { fmt$ } from '../utils';

// Existing (migrated) analyzers
import * as processMismatch    from '../analyzers/processMismatch';
import * as mistakeTaxonomy    from '../analyzers/mistakeTaxonomy';
import * as performanceDecay   from '../analyzers/performanceDecay';
import * as directionAsymmetry from '../analyzers/directionAsymmetry';
import * as confluenceConsensus from '../analyzers/confluenceConsensus';
import * as accountAsymmetry   from '../analyzers/accountAsymmetry';
// New analyzers (Phase 1)
import * as emotionalTilt      from '../analyzers/emotionalTilt';
import * as baselineDrift      from '../analyzers/baselineDrift';
import * as thesisValidation   from '../analyzers/thesisValidation';

const ANALYZERS = [
  processMismatch,
  mistakeTaxonomy,
  performanceDecay,
  directionAsymmetry,
  confluenceConsensus,
  accountAsymmetry,
  emotionalTilt,
  baselineDrift,
  thesisValidation
];

/**
 * Build a reasonable fallback observation when no analyzer fired. Keeps
 * the UI useful for sessions with too little data to detect patterns.
 *
 * @param {{session, enriched, summary}} ctx
 */
const fallbackObservation = ({ session, enriched, summary }) => {
  if (!session || !Array.isArray(enriched)) return null;
  if (enriched.length === 0) {
    return makeObservation({
      id: `fallback:${session.id}:empty`,
      analyzer: 'fallback',
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: 'No trades in this session yet',
      detail: 'Add trades to this group to unlock coaching observations.',
      confidence: 1,
      priority: 10
    });
  }

  const { wins, losses, total, winRate } = summary;

  if (total > 0 && winRate >= 0.5) {
    return makeObservation({
      id: `fallback:${session.id}:solid`,
      analyzer: 'fallback',
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.PROCESS,
      title: `Solid group: ${wins}W/${losses}L for ${fmt$(total)}`,
      detail: 'Nothing screams off-pattern — keep doing what worked.',
      action: 'Lock the session\'s shared context (setup, time, confluence) in Lessons so it stays repeatable.',
      confidence: 0.5,
      priority: 30
    });
  }

  if (total < 0) {
    return makeObservation({
      id: `fallback:${session.id}:red`,
      analyzer: 'fallback',
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `${wins}W/${losses}L for ${fmt$(total)}`,
      detail: 'No single dominant pattern fired — capture specific lessons before this slips into "just a bad day."',
      action: 'Pick one trade from this group and write what you\'d do differently in 1 sentence.',
      confidence: 0.5,
      priority: 30
    });
  }

  return makeObservation({
    id: `fallback:${session.id}:even`,
    analyzer: 'fallback',
    scope: 'session',
    scopeId: session.id,
    severity: SEVERITY.INFO,
    category: CATEGORY.PROCESS,
    title: `Even outcome: ${wins}W/${losses}L`,
    detail: 'Use the Lessons field to lock in what made the difference between winners and losers.',
    confidence: 0.5,
    priority: 25
  });
};

/**
 * Analyze a single session. Returns ranked observations (top 3 by default).
 *
 * @param {Object} args
 * @param {Object} args.session           The session object (requires id)
 * @param {Array}  args.trades            Ideally already filtered to this session
 * @param {Array}  args.allTrades         Full trade history (for baseline analyzers)
 * @param {Array}  args.allConfluences    User's confluence library
 * @param {Array}  args.allMistakes       User's mistakes library
 * @param {Function} args.getTradeNetPnL  (trade, account) → { netPnl }
 * @param {Function} args.getAccountById  (accountId) → account|null
 * @param {boolean} args.hideAccounts     Mask account names in output
 * @param {number} [args.top=3]           How many observations to return
 * @returns {Observation[]}
 */
export const analyzeSession = ({
  session,
  trades = [],
  allTrades = [],
  allConfluences = [],
  allMistakes = [],
  // setupDetails powers confluenceConsensus's "is this already pinned?" check —
  // without it, the Pin button on the session card briefly flashes "Pinned ✓"
  // then reverts to "Pin to..." because the analyzer keeps emitting the
  // action on the next render. With setupDetails, we filter the action out
  // after the pin succeeds and the button cleanly disappears.
  setupDetails = {},
  getTradeNetPnL,
  getAccountById,
  hideAccounts = false,
  top = 3
} = {}) => {
  if (!session || !session.id) return [];

  // Scope-filter to this session if caller passed an unfiltered array
  const sessionTrades = trades.some(t => t.sessionId === session.id)
    ? trades.filter(t => t.sessionId === session.id)
    : trades;

  const enriched = withNetPnl(sessionTrades, getTradeNetPnL, getAccountById);
  const summary = summarizePnl(enriched);

  const ctx = {
    session,
    trades: sessionTrades,
    enriched,
    summary,
    allTrades,
    allConfluences,
    allMistakes,
    setupDetails,
    getTradeNetPnL,
    getAccountById,
    hideAccounts
  };

  const observations = [];
  for (const analyzer of ANALYZERS) {
    try {
      const result = analyzer.analyze(ctx);
      if (Array.isArray(result)) observations.push(...result.filter(Boolean));
      else if (result) observations.push(result);
    } catch (err) {
      // One bad analyzer shouldn't take down the whole engine
      console.warn(`[Coach] analyzer "${analyzer.key || 'unknown'}" threw:`, err);
    }
  }

  // If nothing fired, return a single graceful fallback
  if (observations.length === 0) {
    const fb = fallbackObservation(ctx);
    return fb ? [fb] : [];
  }

  return topObservations(observations, top);
};

export default { analyzeSession };
